import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserPlus, Link as LinkIcon, Users, ArrowLeft, GraduationCap, Info } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { error as logError } from '../../../utils/logger';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { BulkImport } from './BulkImport';
import { NewStudentStep } from './add-student/NewStudentStep';
import { LinkExistingStep } from './add-student/LinkExistingStep';
import { SuccessStep } from './add-student/SuccessStep';
import { getLinkedLearners, findAccountByLinkCode, isLearnerAccount, validateAddLearnerToClassroom, type ClassroomValidationWarning } from '../../../accounts';
import { type TeacherAccount, type Classroom, type LearnerAccount } from '../../../accounts';
interface AddStudentPanelV2Props {
  activeAccount: TeacherAccount;
  onComplete: () => void;
  onCancel: () => void;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
}
export function AddStudentPanelV2({
  activeAccount,
  onComplete,
  onCancel,
  classrooms = [],
  selectedClassroomId = null
}: AddStudentPanelV2Props) {
  // Singleton store — all consumers share the same state (Zustand refactor s78-s81)
  const {
    accounts,
    createAndLinkStudent,
    linkTeacherToLearnerById,
    addLearnerToClassroom
  } = useAppStore();
  const [step, setStep] = useState<'choose' | 'new' | 'existing' | 'bulk' | 'success'>('choose');
  // Classroom context — auto-resolve if only 1 classroom exists
  const resolvedClassroomId = selectedClassroomId || (classrooms.length === 1 ? classrooms[0].id : null);
  const activeClassroom = resolvedClassroomId ? classrooms.find(c => c.id === resolvedClassroomId) : null;
  // Result state
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  // Validation warnings
  const [linkWarnings, setLinkWarnings] = useState<ClassroomValidationWarning[]>([]);
  const [confirmLink, setConfirmLink] = useState(false);
  const [newStudentName, setNewStudentName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLearner, setSelectedLearner] = useState<LearnerAccount | null>(null);
  const [linkCodeInput, setLinkCodeInput] = useState('');
  const [foundByCode, setFoundByCode] = useState<LearnerAccount | null>(null);
  const [linkMode, setLinkMode] = useState<'search' | 'code'>('search');
  const [fallbackGrade, setFallbackGrade] = useState(activeClassroom ? activeClassroom.groupLevel.toString() : '6');
  const [fallbackClassroomId, setFallbackClassroomId] = useState('');
  const linkedLearners = getLinkedLearners(accounts, activeAccount.id);
  const linkedIds = new Set(linkedLearners.map(l => l.id));
  // All learner accounts NOT yet linked to this teacher
  const availableLearners = useMemo(() => {
    const all: LearnerAccount[] = [];
    for (const acc of Object.values(accounts.accountsById)) {
      if (isLearnerAccount(acc) && !linkedIds.has(acc.id)) {
        all.push(acc);
      }
    }
    return all.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [accounts.accountsById, linkedIds]);
  // Filtered by search
  const filteredLearners = useMemo(() => {
    if (!searchQuery.trim()) return availableLearners;
    const q = searchQuery.toLowerCase();
    return availableLearners.filter(l => l.displayName.toLowerCase().includes(q) || l.username && l.username.toLowerCase().includes(q));
  }, [availableLearners, searchQuery]);
  // ── Handlers ──────────────────────────────────────────────
  const handleLinkSelectedLearner = () => {
    const learner = selectedLearner || foundByCode;
    if (!learner) return;
    setFormError('');
    const classroomId = activeClassroom?.id;
    // Check if already in this classroom
    if (classroomId && activeClassroom) {
      if (activeClassroom.learnerIds.includes(learner.id)) {
        setFormError(`${learner.displayName} zit al in deze klas.`);
        return;
      }
    }
    // Run validation checks (grade mismatch, existing links)
    if (classroomId && !confirmLink) {
      const warnings = validateAddLearnerToClassroom(accounts, activeAccount.id, classroomId, learner.id);
      if (warnings.length > 0) {
        setLinkWarnings(warnings);
        return;
      }
    }
    // Check if already linked to teacher (but not in this classroom)
    if (linkedIds.has(learner.id)) {
      if (classroomId) {
        addLearnerToClassroom(activeAccount.id, classroomId, learner.id);
        setFormSuccess(`${learner.displayName} is aan de klas toegevoegd!`);
        setLinkWarnings([]);
        setConfirmLink(false);
        setStep('success');
        return;
      }
      setFormError(`${learner.displayName} is al aan jou gekoppeld.`);
      return;
    }
    try {
      linkTeacherToLearnerById(activeAccount.id, learner.id);
      if (classroomId) {
        addLearnerToClassroom(activeAccount.id, classroomId, learner.id);
      }
      setFormSuccess(`${learner.displayName} is succesvol ${classroomId ? 'aan de klas' : 'aan je account'} toegevoegd!`);
      setLinkWarnings([]);
      setConfirmLink(false);
      setStep('success');
    } catch (err) {
      setFormError('Er is een fout opgetreden bij het koppelen.');
    }
  };
  const handleLookupCode = () => {
    setFormError('');
    setFoundByCode(null);
    if (!linkCodeInput.trim()) {
      setFormError('Vul een koppelcode in.');
      return;
    }
    const found = findAccountByLinkCode(accounts, linkCodeInput.trim().toUpperCase());
    if (!found || !isLearnerAccount(found)) {
      setFormError('Geen leerling gevonden met deze koppelcode.');
      return;
    }
    setFoundByCode(found);
  };
  const resetForm = () => {
    setFormError('');
    setFormSuccess('');
    setGeneratedCode('');
    setNewStudentName('');
    setSearchQuery('');
    setSelectedLearner(null);
    setLinkCodeInput('');
    setFoundByCode(null);
    setLinkMode('search');
    setLinkWarnings([]);
    setConfirmLink(false);
  };
  const goBack = () => {
    resetForm();
    setStep('choose');
  };
  // ── Create new student handler ─────────────────────────────
  const handleCreateStudent = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!newStudentName.trim()) {
      setFormError('Vul een naam in.');
      return;
    }
    const classroomId = activeClassroom?.id || fallbackClassroomId || undefined;
    const grade = activeClassroom ? activeClassroom.groupLevel : parseInt(fallbackGrade, 10);
    try {
      const {
        studentId,
        linkCode
      } = createAndLinkStudent(activeAccount.id, {
        displayName: newStudentName.trim(),
        grade
      }, classroomId);
      setGeneratedCode(linkCode);
      setFormSuccess(`${newStudentName.trim()} is aangemaakt!`);
      setStep('success');
    } catch (err) {
      logError('Failed to create student', err);
      setFormError('Er is een fout opgetreden bij het aanmaken.');
    }
  };
  // ── Render ────────────────────────────────────────────────
  return <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden flex flex-col" data-id="element-5157">
      {/* Header */}
      <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex items-center justify-between" data-id="element-5158">
        <div className="flex items-center gap-3" data-id="element-5159">
          <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center shadow-sm" data-id="element-5160">
            <GraduationCap className="w-5 h-5" data-id="element-5161" />
          </div>
          <div data-id="element-5162">
            <h3 className="font-bold text-slate-900" data-id="element-5163">Leerling toevoegen</h3>
            <p className="text-xs font-medium text-slate-500" data-id="element-5164">
              {activeClassroom ? `${activeClassroom.name} · Groep ${activeClassroom.groupLevel} · ${activeClassroom.learnerIds.length} leerlingen` : 'Aan jouw account'}
            </p>
          </div>
        </div>
        {step !== 'choose' && step !== 'success' && <button onClick={goBack} className="text-sm font-bold text-slate-500 hover:text-slate-800 flex items-center gap-1 transition-colors px-3 py-1.5 rounded-lg hover:bg-slate-200/50" data-id="element-5165">
            <ArrowLeft className="w-4 h-4" data-id="element-5166" /> Terug
          </button>}
      </div>

      <div className="p-6 sm:p-8 relative min-h-[350px]" data-id="element-5167">
        <AnimatePresence mode="wait" data-id="element-5168">
          {/* ── STEP: Choose ─────────────────────────────── */}
          {step === 'choose' && <motion.div key="choose" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} data-id="element-5169">
              <div className="text-center mb-8" data-id="element-5170">
                <h3 className="text-xl font-black text-slate-900 mb-2" data-id="element-5171">
                  Hoe wil je leerlingen toevoegen?
                </h3>
                <p className="text-slate-500" data-id="element-5172">
                  {activeClassroom ? `Leerlingen worden toegevoegd aan ${activeClassroom.name}.` : 'Kies een methode.'}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5173">
                <button onClick={() => setStep('new')} className="text-left group p-6 rounded-2xl border-2 border-slate-100 hover:border-indigo-500 hover:bg-indigo-50/50 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2" data-id="element-5174">
                  <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-sm" data-id="element-5175">
                    <UserPlus className="w-6 h-6" data-id="element-5176" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-1 group-hover:text-indigo-700 transition-colors" data-id="element-5177">
                    Nieuwe leerling
                  </h4>
                  <p className="text-sm text-slate-500" data-id="element-5178">
                    Maak een nieuw account aan en genereer een koppelcode.
                  </p>
                </button>

                <button onClick={() => setStep('existing')} className="text-left group p-6 rounded-2xl border-2 border-slate-100 hover:border-violet-500 hover:bg-violet-50/50 transition-all focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2" data-id="element-5179">
                  <div className="w-12 h-12 bg-violet-100 text-violet-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-sm" data-id="element-5180">
                    <LinkIcon className="w-6 h-6" data-id="element-5181" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-1 group-hover:text-violet-700 transition-colors" data-id="element-5182">
                    Bestaande koppelen
                  </h4>
                  <p className="text-sm text-slate-500" data-id="element-5183">
                    Zoek een leerling in het systeem of voer een koppelcode in.
                  </p>
                </button>

                <button onClick={() => setStep('bulk')} className="text-left group p-6 rounded-2xl border-2 border-slate-100 hover:border-emerald-500 hover:bg-emerald-50/50 transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2" data-id="element-5184">
                  <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-sm" data-id="element-5185">
                    <Users className="w-6 h-6" data-id="element-5186" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-1 group-hover:text-emerald-700 transition-colors" data-id="element-5187">
                    Meerdere tegelijk
                  </h4>
                  <p className="text-sm text-slate-500" data-id="element-5188">
                    Importeer een namenlijst om meerdere leerlingen toe te
                    voegen.
                  </p>
                </button>
              </div>

              <div className="mt-8 flex justify-center" data-id="element-5189">
                <Button variant="secondary" onClick={onCancel} data-id="element-5190">
                  Annuleren
                </Button>
              </div>
            </motion.div>}

          {/* ── STEP: New Student ────────────────────────── */}
          {step === 'new' && <NewStudentStep activeClassroom={activeClassroom || null} classrooms={classrooms} newStudentName={newStudentName} setNewStudentName={setNewStudentName} fallbackGrade={fallbackGrade} setFallbackGrade={setFallbackGrade} fallbackClassroomId={fallbackClassroomId} setFallbackClassroomId={setFallbackClassroomId} formError={formError} onSubmit={handleCreateStudent} onBack={goBack} data-id="element-5191" />}

          {/* ── STEP: Link Existing ──────────────────────── */}
          {step === 'existing' && <LinkExistingStep activeClassroom={activeClassroom || null} availableLearners={availableLearners} filteredLearners={filteredLearners} searchQuery={searchQuery} setSearchQuery={setSearchQuery} linkMode={linkMode} setLinkMode={setLinkMode} selectedLearner={selectedLearner} setSelectedLearner={setSelectedLearner} linkCodeInput={linkCodeInput} setLinkCodeInput={setLinkCodeInput} foundByCode={foundByCode} setFoundByCode={setFoundByCode} formError={formError} setFormError={setFormError} linkWarnings={linkWarnings} confirmLink={confirmLink} setConfirmLink={setConfirmLink} onLookupCode={handleLookupCode} onLinkSelectedLearner={handleLinkSelectedLearner} onBack={goBack} data-id="element-5192" />}

          {/* ── STEP: Bulk Import ────────────────────────── */}
          {step === 'bulk' && <motion.div key="bulk" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.2
        }} data-id="element-5193">
              {activeClassroom && <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-100 rounded-xl mb-6 max-w-2xl mx-auto" data-id="element-5194">
                  <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-5195" />
                  <p className="text-sm text-emerald-800" data-id="element-5196">
                    Alle leerlingen worden toegevoegd aan{' '}
                    <strong data-id="element-5197">{activeClassroom.name}</strong> (Groep{' '}
                    {activeClassroom.groupLevel}).
                  </p>
                </div>}
              <BulkImport teacherId={activeAccount.id} onComplete={onComplete} onCancel={goBack} classrooms={classrooms} selectedClassroomId={activeClassroom ? activeClassroom.id : selectedClassroomId} data-id="element-5198" />
            </motion.div>}

          {/* ── STEP: Success ────────────────────────────── */}
          {step === 'success' && <SuccessStep formSuccess={formSuccess} generatedCode={generatedCode} onAddAnother={() => {
          resetForm();
          setStep('choose');
        }} onComplete={onComplete} data-id="element-5199" />}
        </AnimatePresence>
      </div>
    </div>;
}